import { useEffect, useRef, useState } from "react";
import api, { API, withCompany } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload, Trash2, FileArchive, FileText, FileImage, Download } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ProjectSelect from "@/components/ProjectSelect";
import { fmtDate } from "@/lib/utils";

const CATEGORIES = [{v: "cizim", l: "Çizim"}, {v: "foto", l: "Fotoğraf"}, {v: "sertifika", l: "Sertifika"}, {v: "genel", l: "Genel Belge"}];

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [uploadCategory, setUploadCategory] = useState("genel");
  const [uploadProject, setUploadProject] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/files${filter ? `?project_id=${filter}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [filter]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ category: uploadCategory });
      if (uploadProject) params.append("project_id", uploadProject);
      await api.post(`/files/upload?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dosya yüklendi"); load();
    } catch (err) {
      toast.error("Yükleme başarısız: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Dosyayı silmek istiyor musunuz?")) return;
    await api.delete(`/files/${id}`); toast.success("Silindi"); load();
  };

  const fileIcon = (ct) => {
    if (ct?.startsWith("image/")) return FileImage;
    if (ct === "application/pdf" || ct?.includes("text")) return FileText;
    return FileArchive;
  };

  const sizeFmt = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  const catColor = { cizim: "bg-blue-500/15 text-blue-700 dark:text-blue-400", foto: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", sertifika: "bg-amber-500/15 text-amber-700 dark:text-amber-400", genel: "bg-slate-500/15 text-slate-700 dark:text-slate-300" };

  return (
    <div data-testid="documents-page">
      <PageHeader title="Çizim ve Belgeler" subtitle="Şantiye çizimleri, fotoğraflar ve sertifikalar.">
        <ProjectSelect projects={projects} value={filter} onChange={setFilter} testid="filter-project" />
      </PageHeader>

      <div className="rounded-xl border border-border bg-card p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-semibold mb-2">Kategori</div>
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-semibold mb-2">Proje</div>
            <ProjectSelect projects={projects} value={uploadProject} onChange={setUploadProject} testid="upload-project" />
          </div>
          <input type="file" ref={fileRef} onChange={onUpload} className="hidden" data-testid="file-input" />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
            data-testid="upload-button"
          >
            <Upload className="w-4 h-4 mr-1" /> {uploading ? "Yükleniyor..." : "Dosya Yükle"}
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <FileArchive className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz dosya yok</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map(r => {
            const Icon = fileIcon(r.content_type);
            return (
              <div key={r.id} className="rounded-xl border border-border bg-card p-4 flex gap-3 hover:border-amber-500 transition-colors" data-testid={`file-${r.id}`}>
                <div className="w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" title={r.original_filename}>{r.original_filename}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={catColor[r.category]}>{r.category}</Badge>
                    <span className="text-xs text-muted-foreground font-mono-nums">{sizeFmt(r.size)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtDate(r.created_at)}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <a href={withCompany(`${API}/files/${r.id}/download`)} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center" data-testid={`download-${r.id}`}>
                    <Download className="w-4 h-4" />
                  </a>
                  <button onClick={() => remove(r.id)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center">
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
